import { AlertCircle, CheckCircle2, Loader2, Plus, ShieldAlert, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { PaymentMethodEditor } from './PaymentMethodEditor'
import {
  MAX_PAYMENT_METHODS,
  countMethodsWithAccountData,
  type PaymentMethodDraft,
  type PaymentSettingsErrors,
} from '@/lib/paymentMethods'

export interface PaymentSettingsViewProps {
  methods: PaymentMethodDraft[]
  shareDetails: boolean
  errors: PaymentSettingsErrors
  isDirty: boolean
  isSaving: boolean
  saveError: string | null
  onShareChange: (value: boolean) => void
  onMethodChange: (method: PaymentMethodDraft) => void
  onMethodRemove: (id: string) => void
  onMethodAdd: () => void
  onSave: () => void
}

/** Pantalla de pagos del negocio: la regla del asistente y los métodos con sus datos. Solo presentación. */
export function PaymentSettingsView({
  methods,
  shareDetails,
  errors,
  isDirty,
  isSaving,
  saveError,
  onShareChange,
  onMethodChange,
  onMethodRemove,
  onMethodAdd,
  onSave,
}: PaymentSettingsViewProps) {
  const withData = countMethodsWithAccountData(methods)
  const sharingActive = shareDetails && withData > 0

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <section aria-labelledby="payment-rule-title" className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <h2 id="payment-rule-title" className="text-sm font-bold text-slate-900">
          Regla del asistente
        </h2>
        <div className="mt-3">
          <Switch
            checked={shareDetails}
            disabled={isSaving}
            onChange={(event) => onShareChange(event.target.checked)}
            label="Permitir que el asistente envíe los datos bancarios al cliente"
            description="Desactivado: el asistente no comparte cuentas ni teléfonos; avisa que una persona del equipo enviará los datos de pago. Activado: solo enviará lo que cargues en esta pantalla."
          />
        </div>
        <p
          role="status"
          className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
            sharingActive ? 'bg-emerald-100 text-emerald-900' : 'bg-white text-slate-700'
          }`}
        >
          {sharingActive ? (
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          ) : (
            <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          )}
          <span>
            {sharingActive
              ? `El asistente enviará los datos de ${withData} ${withData === 1 ? 'método' : 'métodos'} cuando el cliente vaya a pagar.`
              : shareDetails
                ? 'La regla está activada, pero aún no hay ninguna cuenta cargada: el asistente no tiene datos que enviar.'
                : 'Los datos de pago los enviará una persona de tu equipo por el chat.'}
          </span>
        </p>
        {errors.form && (
          <p role="alert" className="mt-2 flex items-start gap-2 text-xs font-medium text-rose-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            <span>{errors.form}</span>
          </p>
        )}
      </section>

      <section aria-labelledby="payment-methods-title" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="payment-methods-title" className="text-sm font-bold text-slate-900">
            Métodos de pago de tu negocio
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSaving || methods.length >= MAX_PAYMENT_METHODS}
            onClick={onMethodAdd}
          >
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Agregar método
          </Button>
        </div>

        {methods.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center">
            <p className="text-sm font-medium text-slate-800">Aún no cargaste métodos de pago.</p>
            <p className="mt-1 text-xs text-slate-500">
              Agrega Pago Móvil o transferencia para que el asistente sepa cómo cobrar (y, si lo permites, pueda enviar los datos).
            </p>
          </div>
        ) : (
          methods.map((method, index) => (
            <PaymentMethodEditor
              key={method.id}
              method={method}
              index={index}
              error={errors.methods[method.id]}
              disabled={isSaving}
              onChange={onMethodChange}
              onRemove={() => onMethodRemove(method.id)}
            />
          ))
        )}
      </section>

      {saveError && (
        <p role="alert" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{saveError}</span>
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <p className="flex items-center gap-1.5 text-xs text-slate-500">
          {isDirty ? (
            'Tienes cambios sin guardar.'
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
              Todo guardado.
            </>
          )}
        </p>
        <Button type="button" disabled={!isDirty || isSaving} onClick={onSave} className="bg-[#075E54] hover:bg-[#064c44]">
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {isSaving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>
    </div>
  )
}
